import React, { useContext, useEffect, useMemo, useState } from "react";
import { EditOutlined as EditOutlinedIcon } from "@mui/icons-material";
import { Checkbox, IconButton, Paper } from "@mui/material";
import { delay, isEqual } from "lodash";

import { Node } from "ui";

import { selectLayerConfigByKey, selectVersionConfig } from "fond/api";
import { useMultiLayerEdit } from "fond/draw/MultiLayerEdit/useMultiLayerEdit";
import { SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppSelector } from "fond/utils/hooks";

import { InferredIcon } from "../Legend";
import { MapContext } from "../MapProvider";

import { buildEditableSublayers, mapStyleIdsToMapLayerIds, updateLayerVisibility } from "./helper";

import { Container, Label, Row } from "./LayerSelection.styles";

export interface IButtonProps {
  title?: string;
  icon?: React.ReactNode;
  mode?: string;
}

interface LayerNode extends Node {
  isSublayer?: boolean;
}

const LayerSelection: React.FC = () => {
  const { drawControl, map, isDrawing } = useContext(MapContext);
  const { layerStyleIds } = useMultiLayerEdit();
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));
  const [selectedLayer, setSelectedLayer] = useState<string | undefined>(drawControl.current?.options?.editOptions?.editingLayerId);
  const [visibleLayers, setVisibleLayers] = useState<Set<string>>(new Set(drawControl.current?.options?.visibleLayerIds || []));
  const [editingLayers, setEditingLayers] = useState<Set<string>>(new Set(drawControl.current?.options?.editOptions?.editingLayerIds || []));

  /**
   * Generates a list of editable layers based on the drawing control's option.allLayers and the project's version config.
   * Each layer is represented as a node with a value, label, and icon for use in the checkbox tree.
   * When ShowSublayers is true, sublayers are also included in the list.
   */
  const layerOptions = drawControl.current?.options?.editOptions?.allLayers;

  // Get layer data from Redux state
  // Use LayerConfig directly since it's always available (even when Layer doesn't exist yet)
  const layerData = useAppSelector((state) => {
    if (!layerOptions) return [];
    return layerOptions.map((layer) => ({
      layerOption: layer,
      layerConfig: selectLayerConfigByKey(state, { versionId, layerKey: layer.ID }),
    }));
  }, isEqual);

  // All layers including hidden ones (for visibility initialization)
  const allLayersIncludingHidden = useMemo(() => {
    const layerConfigs: LayerNode[] = [];

    if (!config) return layerConfigs;

    layerData.forEach(({ layerOption, layerConfig }) => {
      if (layerConfig) {
        layerConfigs.push({
          value: layerOption.ID,
          label: layerConfig?.Label,
          icon: <InferredIcon entity={layerConfig} config={config} />,
          isSublayer: false,
        });

        // If ShowSublayers is true, add sublayers to the list
        if (layerOption.ShowSublayers && layerConfig.Children && layerConfig.Children.length > 0) {
          layerConfig.Children.forEach((sublayerId) => {
            const sublayerConfig = config.Data.entities[sublayerId] as SublayerConfig;
            if (sublayerConfig && sublayerConfig.Type === "SUBLAYER") {
              layerConfigs.push({
                value: sublayerId,
                label: sublayerConfig.Label,
                icon: <InferredIcon entity={sublayerConfig} config={config} />,
                isSublayer: true,
              });
            }
          });
        }
      }
    });

    return layerConfigs;
  }, [layerData, config]);

  // Visible layers in UI (excludes hidden layers)
  const allLayers = useMemo(() => {
    return allLayersIncludingHidden.filter((layer) => {
      const layerOption = layerData.find((ld) => ld.layerOption.ID === layer.value)?.layerOption;
      return !layerOption?.Hide;
    });
  }, [allLayersIncludingHidden, layerData]);

  useEffect(() => {
    setSelectedLayer(drawControl.current?.options?.editOptions?.editingLayerId);
  }, [drawControl, drawControl.current?.options?.editOptions?.editingLayerId, map]);

  // Initialize all layers as visible (only once when empty)
  // Include ALL layers (even hidden ones) and all sublayers regardless of ShowSublayers setting
  useEffect(() => {
    if (allLayersIncludingHidden.length > 0 && visibleLayers.size === 0) {
      const allLayerIds = new Set(allLayersIncludingHidden.map((layer) => layer.value));

      // Also include all sublayers, even if ShowSublayers is false
      // This ensures sublayers are visible when their parent is visible
      if (config) {
        layerData.forEach(({ layerConfig }) => {
          if (layerConfig?.Children) {
            layerConfig.Children.forEach((sublayerId) => {
              const sublayerConfig = config.Data.entities[sublayerId] as SublayerConfig;
              if (sublayerConfig?.Type === "SUBLAYER") {
                allLayerIds.add(sublayerId);
              }
            });
          }
        });
      }

      const visibleIds = Array.from(allLayerIds);
      setVisibleLayers(new Set(visibleIds));

      // Store in drawControl options for snapGetFeatures
      if (drawControl.current?.options) {
        drawControl.current.options.visibleLayerIds = visibleIds;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLayersIncludingHidden.length, config, layerData]);

  // Initialize editable layers (only once when empty)
  // Sets editingLayerIds on the draw control so that isFeatureInEditableScope
  // properly filters which features can be selected. Without this, editingLayerIds
  // stays undefined and all features are editable regardless of layer config.
  useEffect(() => {
    const existingEditingLayerIds = drawControl.current?.options?.editOptions?.editingLayerIds;
    if (allLayers.length > 0 && editingLayers.size === 0 && existingEditingLayerIds === undefined) {
      const editableIds = new Set(allLayers.map((layer) => layer.value));
      setEditingLayers(editableIds);

      const editableSublayers = buildEditableSublayers(editableIds, config);

      if (drawControl.current?.options?.editOptions) {
        drawControl.current.options.editOptions.editingLayerIds = Array.from(editableIds);
        drawControl.current.options.editOptions.editableSublayers = editableSublayers;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLayers.length, config]);

  // Update map layer visibility when visibleLayers changes
  // Toggles visibility for layer-specific styles (including validation styles)
  // Uses cached layerStyleIds from context for efficient lookup
  useEffect(() => {
    if (!map || !layerStyleIds) return;

    const style = map.getStyle();
    if (!style) return;

    // Find all draw layers by source property (mapbox-gl-draw adds .cold/.hot suffixes)
    const drawLayers = style.layers.filter(
      (layer) => "source" in layer && (layer.source === "mapbox-gl-draw-cold" || layer.source === "mapbox-gl-draw-hot")
    );

    // Map style IDs to their actual map layer IDs (with .cold/.hot suffixes)
    const styleIdToMapLayerIds = mapStyleIdsToMapLayerIds(layerStyleIds, drawLayers);

    // Toggle visibility using the mapped style IDs
    updateLayerVisibility(map, layerStyleIds, visibleLayers, styleIdToMapLayerIds);
  }, [map, visibleLayers, layerStyleIds]);

  const handleToggleVisibility = (layerId: string) => {
    setVisibleLayers((prev) => {
      const newSet = new Set(prev);
      const isVisible = newSet.has(layerId);

      if (isVisible) {
        newSet.delete(layerId);
      } else {
        newSet.add(layerId);
      }

      // Check if this is a parent layer and toggle all its sublayers too
      if (config) {
        const layerInfo = layerData.find((ld) => ld.layerOption.ID === layerId);
        if (layerInfo?.layerConfig?.Children) {
          layerInfo.layerConfig.Children.forEach((sublayerId) => {
            const sublayerConfig = config.Data.entities[sublayerId] as SublayerConfig;
            if (sublayerConfig?.Type === "SUBLAYER") {
              if (isVisible) {
                newSet.delete(sublayerId);
              } else {
                newSet.add(sublayerId);
              }
            }
          });
        }
      }

      // Store visible layers in drawControl options so snapGetFeatures can access it
      if (drawControl.current?.options) {
        drawControl.current.options.visibleLayerIds = Array.from(newSet);
      }

      return newSet;
    });
  };

  const handleToggleEditing = (layerId: string) => {
    setEditingLayers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(layerId)) {
        newSet.delete(layerId);
      } else {
        newSet.add(layerId);
      }

      // Build sublayer metadata for editable sublayers
      const editableSublayers = buildEditableSublayers(newSet, config);

      // Store editing layers and sublayer metadata in drawControl options
      if (drawControl.current?.options?.editOptions) {
        drawControl.current.options.editOptions.editingLayerIds = Array.from(newSet);
        drawControl.current.options.editOptions.editableSublayers = editableSublayers;
      }

      // Defer the event firing to avoid updating other components during render
      delay(() => {
        map?.fire("draw.editingLayersChange", {
          editingLayerIds: Array.from(newSet),
          editableSublayers,
        });
      }, 0);

      return newSet;
    });
  };

  /**
   * Handles the event when a layer is selected in the radio button group.
   * Fires a custom event that other components (like MultiLayerEditHandler) will handle.
   * @param id - The ID of the layer or sublayer that has been selected.
   */
  const handleOnSelect = (id: string) => {
    if (drawControl.current && drawControl.current.options) {
      // Check if this is a sublayer by looking it up in the config
      const sublayerConfig = config?.Data.entities[id] as SublayerConfig | undefined;
      const isSublayer = sublayerConfig?.Type === "SUBLAYER";

      // For sublayers, use the sublayer's Key (which is copied from parent) as the parentLayerId
      const parentLayerId = isSublayer && sublayerConfig ? sublayerConfig.Key : id;

      // Find the editing layer using the parent layer ID
      const editingLayer = drawControl.current.options.editOptions?.allLayers?.find((layer) => layer.ID === parentLayerId);

      setSelectedLayer(id);

      // Defer the event firing to avoid updating other components during render
      delay(() => {
        map?.fire("draw.layerchange", {
          layerId: id,
          parentLayerId: isSublayer ? parentLayerId : undefined,
          geometryType: editingLayer?.GeometryType,
          editingLayer,
          sublayerFilter: isSublayer && sublayerConfig?.FilterConfiguration?.Mapbox ? sublayerConfig.FilterConfiguration.Mapbox : undefined,
        });
      }, 0);
    }
  };

  if (!isDrawing || !drawControl.current?.options?.editOptions?.allLayers || allLayers.length === 0) return null;

  return (
    <Container>
      <Paper sx={{ p: 1 }} elevation={3}>
        {allLayers.map(({ label, value, icon, isSublayer }) => {
          // Each layer and sublayer has independent visibility
          const isVisible = visibleLayers.has(value);

          return (
            <Row key={value} selected={selectedLayer === value} onClick={() => handleOnSelect(value)} sx={{ pl: isSublayer ? 2 : 0 }}>
              <Checkbox
                checked={isVisible}
                onChange={(e) => {
                  e.stopPropagation();
                  handleToggleVisibility(value);
                }}
                size="small"
                sx={{ p: 0.5 }}
                title="Toggle layer visibility"
              />
              <Label editing={editingLayers.has(value)}>
                {icon}
                {label}
              </Label>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleEditing(value);
                }}
                color={editingLayers.has(value) ? "primary" : "default"}
                title="Toggle layer editing"
                sx={{ ml: "auto", p: 0 }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Row>
          );
        })}
      </Paper>
    </Container>
  );
};

export default LayerSelection;
